import type { MazeStagePixiPalette } from '@/theme/maze-stage-palette';
import * as PIXI from 'pixi.js';

/**
 * Per-wall-cube proximity neon. Inset `rgba(160,250,244,0.5)` with ~2.5px blur lives on
 * `wallGlowInner` (Pixi BlurFilter). Light cyan border `rgba(186,255,250,1)` and outer hints
 * use the same family on `wallGlow` for a crisp rim on top.
 */

/** Figma wall neon: index 0 = closest (brightest), 4 = furthest. */
export interface MazeWallNeonTier {
  readonly borderAlpha: number;
  readonly insetCyanAlpha: number;
  readonly outerGlowAlpha: number;
}

/** Matches Figma CSS stacks (border / inset cyan / outer). */
export const MAZE_WALL_NEON_TIERS: readonly MazeWallNeonTier[] = [
  { borderAlpha: 1, insetCyanAlpha: 0.5, outerGlowAlpha: 0.75 },
  { borderAlpha: 0.8, insetCyanAlpha: 0.4, outerGlowAlpha: 0.4 },
  { borderAlpha: 0.6, insetCyanAlpha: 0.3, outerGlowAlpha: 0.3 },
  { borderAlpha: 0.4, insetCyanAlpha: 0.2, outerGlowAlpha: 0.2 },
  { borderAlpha: 0.2, insetCyanAlpha: 0.1, outerGlowAlpha: 0.1 },
];

/** `rgba(186, 255, 250, 1)` — light cyan neon border / outer rim. */
/** `rgba(160, 250, 244, …)` — inset inner shadow (alpha from tier, max 0.5). */
const TOP_SHADE_ALPHA = 0.25;

/** Sharp inner rim on the non-blurred layer (after blurred inset). */
const INSET_CYAN_STROKE_WIDTH = 1.25;

/** Tight outer hint before the final twin border. */
const OUTER_HALO_WIDTH = 2;
const OUTER_HALO_ALPHA_FACTOR = 0.28;

/**
 * Figma `inset 0 0 2.5px 2px rgba(160,250,244,0.5)` — inner-aligned stroke width ≈ 2×spread
 * plus blur footprint; blurred by BlurFilter(2.5) on `wallGlowInner`.
 */
const INNER_SHADOW_STROKE_WIDTH = 6;

// Merged two rings into one to halve outer-ring draw calls per wall cell.
const WALL_OUTER_SHADOW_SINGLE: { readonly w: number; readonly factor: number } = { w: 4, factor: 0.14 };

/** Distance-to-tier mapping: 0 = closest (brightest), 4 = furthest. Exported for unit tests. */
export function mazeWallNeonTierFromDistance(dist: number, maxRadius: number): number {
  return Math.min(4, Math.floor((dist / maxRadius) * 5));
}

export function drawMazeWallProximityNeon(params: {
  readonly wallGlowInner: PIXI.Graphics;
  readonly wallGlow: PIXI.Graphics;
  readonly grid: number[][];
  readonly playerX: number;
  readonly playerY: number;
  readonly cols: number;
  readonly rows: number;
  readonly maxRadius: number;
  readonly cellSize: number;
  readonly wallPx: number;
  readonly cornerRadius: number;
  readonly palette: MazeStagePixiPalette;
}): void {
  const {
    wallGlowInner,
    wallGlow,
    grid,
    playerX,
    playerY,
    cols,
    rows,
    maxRadius,
    cellSize: cs,
    wallPx,
    cornerRadius,
  } = params;

  const maxRadiusSq = maxRadius * maxRadius;

  const minCol = Math.max(0, Math.floor((playerX - maxRadius) / cs));
  const maxCol = Math.min(cols - 1, Math.floor((playerX + maxRadius) / cs));
  const minRow = Math.max(0, Math.floor((playerY - maxRadius) / cs));
  const maxRow = Math.min(rows - 1, Math.floor((playerY + maxRadius) / cs));

  for (let r = minRow; r <= maxRow; r += 1) {
    const rowCells = grid[r];
    if (!rowCells) continue;
    for (let c = minCol; c <= maxCol; c += 1) {
      if (rowCells[c] !== 1) {
        continue;
      }

      const wallX = c * cs + cs / 2 - wallPx / 2;
      const wallY = r * cs + cs / 2 - wallPx / 2;
      const wallCenterX = c * cs + cs / 2;
      const wallCenterY = r * cs + cs / 2;

      const dx = playerX - wallCenterX;
      const dy = playerY - wallCenterY;
      const distSq = dx * dx + dy * dy;

      if (distSq >= maxRadiusSq) {
        continue;
      }

      const dist = Math.sqrt(distSq);
      const tier = MAZE_WALL_NEON_TIERS[mazeWallNeonTierFromDistance(dist, maxRadius)];
      const { palette } = params;

      wallGlowInner.lineStyle(
        INNER_SHADOW_STROKE_WIDTH,
        palette.wallNeonInset,
        tier.insetCyanAlpha,
        0,
      );
      wallGlowInner.drawRoundedRect(wallX, wallY, wallPx, wallPx, cornerRadius);

      wallGlow.lineStyle(WALL_OUTER_SHADOW_SINGLE.w, palette.wallNeonBorder, tier.outerGlowAlpha * WALL_OUTER_SHADOW_SINGLE.factor, 1);
      wallGlow.drawRoundedRect(wallX, wallY, wallPx, wallPx, cornerRadius);

      wallGlow.lineStyle(
        OUTER_HALO_WIDTH,
        palette.wallNeonBorder,
        tier.outerGlowAlpha * OUTER_HALO_ALPHA_FACTOR,
        1,
      );
      wallGlow.drawRoundedRect(wallX, wallY, wallPx, wallPx, cornerRadius);

      wallGlow.lineStyle(
        INSET_CYAN_STROKE_WIDTH,
        palette.wallNeonInset,
        tier.insetCyanAlpha * 0.58,
        0,
      );
      wallGlow.drawRoundedRect(wallX, wallY, wallPx, wallPx, cornerRadius);

      wallGlow.lineStyle(0);
      wallGlow.lineStyle(1, palette.wallNeonTopShade, TOP_SHADE_ALPHA, 0.5);
      wallGlow.moveTo(wallX + 1, wallY + 0.75);
      wallGlow.lineTo(wallX + wallPx - 1, wallY + 0.75);
      wallGlow.lineStyle(0);

      const borderA = tier.borderAlpha;
      wallGlow.lineStyle(1, palette.wallNeonBorder, borderA, 1);
      wallGlow.drawRoundedRect(wallX, wallY, wallPx, wallPx, cornerRadius);
      wallGlow.lineStyle(1, palette.wallNeonBorder, Math.min(1, borderA * 0.92 + 0.06), 0);
      wallGlow.drawRoundedRect(wallX, wallY, wallPx, wallPx, cornerRadius);
    }
  }

  wallGlowInner.lineStyle(0);
  wallGlow.lineStyle(0);
}
